/**
 * Storage commerce and entitlements, on the wire.
 *
 * The one rule this module exists to keep: **there is exactly one pricing
 * truth.** `docs/product/pricing.md` is it, `pricing.ts` is its machine-
 * readable LOCKED subset, and a test parses the document and fails naming the
 * cell when the two disagree. So the catalog here is *derived* from
 * `STORAGE_BOXES` rather than retyped, and the database carries no price
 * column at all. A retyped price is a second truth with no guard on it, and it
 * drifts on the day somebody edits one of the two.
 *
 * The second rule is that entitlement is a server fact. Nothing in these
 * shapes lets a client assert it bought something: a purchase-success callback
 * is a hint to go and reconcile, never a grant.
 */
import { z } from 'zod';
import { ALLOWANCES, PLANS, type Plan, type PlanAllowances, STORAGE_BOXES } from './pricing.js';
import { EntityId, IsoDatetime } from './primitives.js';
import { BYTES_PER_GB, BillingChannel, StorageCadence } from './storage.js';

export { BillingChannel, StorageCadence };

export const PlanKey = z.enum(['free', 'home', 'household', 'private', 'private_plus']);
export type PlanKey = z.infer<typeof PlanKey>;

export const BillingProductKind = z.enum(['base_plan', 'storage_box']);
export type BillingProductKind = z.infer<typeof BillingProductKind>;

export const BillingPurchaseState = z.enum([
  'pending',
  'active',
  'grace',
  'on_hold',
  'canceled',
  'expired',
  'refunded',
  'revoked',
]);
export type BillingPurchaseState = z.infer<typeof BillingPurchaseState>;

/** `storage_box_50`, `storage_box_100`, `storage_box_200` — the logical SKU a
 *  provider product maps onto, and the id every grant records. */
export function storageBoxSku(capacityGb: number): string {
  return `storage_box_${capacityGb}`;
}

export function planSku(planKey: Plan['key']): string {
  return `plan_${planKey}`;
}

export interface StorageBoxCatalogEntry {
  logicalSku: string;
  capacityGb: number;
  capacityBytes: number;
  monthlyUsd: number;
  /** Twelve monthly payments up front. Not a permanent annual discount, and
   *  never to be described as one. */
  annualUsd: number;
}

/**
 * The Storage Box catalog, derived from the locked pricing table.
 *
 * Derived rather than declared so that editing `docs/product/pricing.md` and
 * `pricing.ts` together is the only way a price here can change — there is no
 * third place to forget.
 */
export const STORAGE_BOX_CATALOG: readonly StorageBoxCatalogEntry[] = STORAGE_BOXES.map((box) => ({
  logicalSku: storageBoxSku(box.capacityGb),
  capacityGb: box.capacityGb,
  capacityBytes: box.capacityGb * BYTES_PER_GB,
  monthlyUsd: box.monthlyUsd,
  annualUsd: box.annualUsd,
}));

/** Self-service ceilings. Beyond either, the answer is Private Plus, not a
 *  bigger self-service purchase. */
export const STORAGE_BOX_SELF_SERVICE = {
  maxBoxes: 10,
  maxAddedGb: 2000,
} as const;

/**
 * `GET /v1/billing/storage-catalog`.
 *
 * `price_usd` is the approved web reference price. A native surface shows the
 * store's own localized price instead and must never link a UAE mobile user to
 * an external checkout — which is why `provider_product_id` is here and a
 * checkout URL is not.
 */
export const StorageCatalogResponse = z.object({
  currency: z.literal('USD'),
  cadence_locked_to: StorageCadence.nullable(),
  max_boxes: z.number().int().positive(),
  max_added_gb: z.number().int().positive(),
  boxes_owned: z.number().int().nonnegative(),
  items: z.array(
    z.object({
      logical_sku: z.string().max(64),
      capacity_gb: z.number().int().positive(),
      capacity_bytes: z.number().int().positive(),
      monthly_usd: z.number(),
      annual_usd: z.number(),
      /** Null on the web, where the USD reference price is what is shown. */
      provider_product_id: z.string().nullable(),
      /** Present on a native surface: what the store will actually charge, in
       *  the storefront's own currency and formatting. */
      localized_price: z.string().nullable(),
      purchasable: z.boolean(),
      /** Why not, when `purchasable` is false. */
      blocked_reason: z
        .enum(['base_plan_required', 'box_limit_reached', 'cadence_mismatch', 'not_billing_owner'])
        .nullable(),
    }),
  ),
});
export type StorageCatalogResponse = z.infer<typeof StorageCatalogResponse>;

export const StorageCheckoutRequest = z.object({
  logical_sku: z.string().max(64),
  cadence: StorageCadence,
  /** One box per call. Quantity is a provider concept; repeated purchase is a
   *  repeated call, so a partial failure cannot half-grant. */
  idempotency_key: z.string().min(8).max(128),
});
export type StorageCheckoutRequest = z.infer<typeof StorageCheckoutRequest>;

/**
 * What a channel hands back to start a purchase.
 *
 * Stripe returns a hosted checkout URL. Apple and Google return the product id
 * the native store must be asked for — no URL, because sending a UAE mobile
 * user to external checkout for cloud storage is a hard prohibition.
 */
export const StorageCheckoutResponse = z.object({
  channel: BillingChannel,
  checkout_url: z.string().url().nullable(),
  provider_product_id: z.string().nullable(),
  /** Opaque, and never carrying household names, emails or worker data. */
  provider_account_ref: z.string().nullable(),
});
export type StorageCheckoutResponse = z.infer<typeof StorageCheckoutResponse>;

/**
 * A verified provider transaction, reported to a client.
 *
 * `state` is derived only from the last successfully fetched authoritative
 * provider state. A notification never sets it — a notification is a trigger
 * to go and ask.
 */
export const BillingPurchaseView = z.object({
  id: EntityId.billingPurchase,
  channel: BillingChannel,
  state: BillingPurchaseState,
  cadence: StorageCadence.nullable(),
  purchased_at: IsoDatetime.nullable(),
  paid_through_at: IsoDatetime.nullable(),
  canceled_at: IsoDatetime.nullable(),
  items: z.array(
    z.object({
      logical_sku: z.string().max(64),
      ordinal: z.number().int().nonnegative(),
      quantity: z.number().int().positive(),
    }),
  ),
});
export type BillingPurchaseView = z.infer<typeof BillingPurchaseView>;

// ---------------------------------------------------------------------------
// Entitlements: what this household may do this month, and how much is left.
// ---------------------------------------------------------------------------

/**
 * The units with a published monthly allowance — the same five
 * `usage_events` counts (migration 0082). Storage is deliberately not one of
 * them: it is a level, not an event stream, and it is reported by
 * `GET /v1/vault/usage` instead.
 */
export const AllowanceUnit = z.enum([
  'translation_event',
  'ai_action',
  'receipt_workflow',
  'voice_seconds',
  'whatsapp_rescue',
]);
export type AllowanceUnit = z.infer<typeof AllowanceUnit>;

/**
 * One allowance line as an owner's "what is left this month" screen reads it.
 *
 * `limit: null` means contractual (Private Plus) rather than unlimited, and a
 * surface must say so rather than rendering an infinity symbol — the
 * commercial document has no unlimited tier and a screen that implies one is
 * an unbounded bill.
 */
export const AllowanceStatus = z.object({
  unit: AllowanceUnit,
  used: z.number().int().nonnegative(),
  limit: z.number().int().nonnegative().nullable(),
  remaining: z.number().int().nonnegative().nullable(),
  /** True when this specific capability is paused. Only this one — an
   *  exhausted allowance never stops manual task management. */
  exhausted: z.boolean(),
});
export type AllowanceStatus = z.infer<typeof AllowanceStatus>;

/**
 * `GET /v1/billing/entitlements`. The single answer to "may this household do
 * this right now", for every surface that has to ask.
 *
 * The period bounds come from `apps/api/src/usage/period.ts` — half-open whole
 * months from the customer's billing day in the household's timezone. They are
 * returned rather than recomputed on a client, because a client that computes
 * its own month boundary will disagree with the server on exactly the day a
 * household is refused.
 */
export const EntitlementsResponse = z.object({
  plan: PlanKey,
  /** False for a canceled or never-subscribed household. Read, export and
   *  billing stay available; new byte-producing and paid actions do not. */
  active: z.boolean(),
  period_start: IsoDatetime,
  period_end: IsoDatetime,
  worker_seats: z.object({
    used: z.number().int().nonnegative(),
    /** Includes seats a valid invitation is holding for seven days. */
    reserved: z.number().int().nonnegative(),
    limit: z.number().int().nonnegative().nullable(),
  }),
  storage: z.object({
    used_bytes: z.number().int().nonnegative(),
    capacity_bytes: z.number().int().nonnegative(),
    blocks_new_bytes: z.boolean(),
  }),
  allowances: z.array(AllowanceStatus),
});
export type EntitlementsResponse = z.infer<typeof EntitlementsResponse>;

/** The published allowance for a unit on a plan, in the unit's own base.
 *  Voice is published in minutes and metered in seconds, and that conversion
 *  lives here so it happens exactly once. */
export function allowanceFor(planKey: Plan['key'], unit: AllowanceUnit): number | null {
  const a: PlanAllowances | null = ALLOWANCES[planKey];
  if (!a) return null;
  switch (unit) {
    case 'translation_event':
      return a.translations;
    case 'ai_action':
      return a.aiActions;
    case 'receipt_workflow':
      return a.receiptScans;
    case 'voice_seconds':
      return a.voiceMinutes * 60;
    case 'whatsapp_rescue':
      return a.whatsappRescueEvents;
  }
}

/** Included cloud storage for a plan, in bytes. `null` on Private Plus, whose
 *  capacity is contractual and must never be guessed at. */
export function includedStorageBytes(planKey: Plan['key']): number | null {
  const plan = PLANS.find((p) => p.key === planKey);
  if (!plan || plan.storageGb === null) return null;
  return plan.storageGb * BYTES_PER_GB;
}

/** Worker seats a plan includes. `null` is contractual, never unlimited. */
export function includedWorkerSeats(planKey: Plan['key']): number | null {
  return PLANS.find((p) => p.key === planKey)?.workers ?? null;
}

export const BillingErrorCode = z.enum([
  'NOT_BILLING_OWNER',
  'BASE_PLAN_REQUIRED',
  'CHANNEL_LOCKED',
  'CADENCE_LOCKED',
  'BOX_LIMIT_REACHED',
  'PROVIDER_UNVERIFIED',
  'PROVIDER_UNAVAILABLE',
  'CONTENT_DOES_NOT_FIT',
]);
export type BillingErrorCode = z.infer<typeof BillingErrorCode>;
